import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { apiClient, API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { toUtcWallClock, formatEventForViewer, formatInstantForViewer, eventLocalDateKey, getViewerTimezone, resolveTimezoneFromState, nowWallClockInViewerZone } from '../utils/dateTime';
import './CalendarPage.css';
import { getServiceTypeLabel } from '../utils/serviceTypeLabel';

// ── Constants ─────────────────────────────────────────────────────────────────
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DURATIONS = ['30 minutes', '1 hour', '2 hours', '3 hours', '4 hours', 'All day'];

// Jobs shown on the calendar — mirrors JobsPage's TAB_STATUSES buckets minus
// DECLINED/CANCELLED, which don't represent scheduled work.
const JOB_STATUSES = ['PENDING', 'ACCEPTED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED'];

// Same bucket mapping as JobsPage.TAB_STATUSES, used to land on the right tab
// when a calendar job card routes to /jobs (status changes happen there, not here).
const STATUS_TAB = {
  PENDING: 'requests',
  ACCEPTED: 'active', SCHEDULED: 'active', IN_PROGRESS: 'active',
  COMPLETED: 'completed',
};

const STATUS_PILL = {
  PENDING:     { label: 'New Request', cls: 'cp-pill-new' },
  ACCEPTED:    { label: 'Confirmed',   cls: 'cp-pill-confirmed' },
  SCHEDULED:   { label: 'Confirmed',   cls: 'cp-pill-confirmed' },
  IN_PROGRESS: { label: 'In Progress', cls: 'cp-pill-inprogress' },
  COMPLETED:   { label: 'Done',        cls: 'cp-pill-done' },
};

// Dot/bar color by service type — legend categories are SP-specific and
// intentionally differ from the main app's transaction-based legend.
const TYPE_COLOR = {
  'home inspection': '#5B47D6',
  'inspection':       '#5B47D6',
  'photography':      '#3B82F6',
  'repairs':          '#DC2626',
  'repair':           '#DC2626',
  'staging':          '#D97706',
};
const NEW_REQUEST_COLOR = '#DC2626';
const DEFAULT_TYPE_COLOR = '#5B47D6';

const LEGEND = [
  { label: 'Inspection',  color: TYPE_COLOR.inspection },
  { label: 'Photography', color: TYPE_COLOR.photography },
  { label: 'Repairs',     color: TYPE_COLOR.repairs },
  { label: 'Staging',     color: TYPE_COLOR.staging },
  { label: 'New Request', color: NEW_REQUEST_COLOR },
];

const colorForJob = (job) => job.status === 'PENDING'
  ? NEW_REQUEST_COLOR
  : (TYPE_COLOR[(job.serviceType || '').toLowerCase()] || DEFAULT_TYPE_COLOR);

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const buildGrid = (year, month) => {
  const firstDay     = new Date(year, month, 1).getDay();
  const daysInMonth  = new Date(year, month + 1, 0).getDate();
  const prevMonthEnd = new Date(year, month, 0).getDate();
  const cells        = [];
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ d: prevMonthEnd - i, other: true });
  for (let d = 1; d <= daysInMonth; d++)   cells.push({ d, other: false });
  const rem = (7 - (cells.length % 7)) % 7;
  for (let i = 1; i <= rem; i++)           cells.push({ d: i, other: true });
  return cells;
};

const monthLabel = (year, month) =>
  new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });

const dayLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const getFirebaseToken = () =>
  new Promise((resolve, reject) => {
    const stored = localStorage.getItem('firebaseToken');
    if (stored) { resolve(stored); return; }
    let unsub;
    const t = setTimeout(() => { if (unsub) unsub(); reject(new Error('timeout')); }, 5000);
    unsub = onAuthStateChanged(auth, async (user) => {
      clearTimeout(t);
      unsub();
      if (user) {
        try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
      } else {
        reject(new Error('no user'));
      }
    });
  });

const emptyForm = (date, time = '10:00') => ({
  title:         '',
  scheduledDate: fmtDate(date),
  scheduledTime: time,
  duration:      '1 hour',
  description:   '',
  location:      '',
});

function extractArray(data) {
  if (Array.isArray(data)) return data;
  return data?.services || data?.content || data?.data || [];
}

// Jobs are placed on the calendar by their due date (requestedDate) — the same
// field the agent's transaction timeline and /calendar use — never by
// createdAt. The SR response carries no scheduledDate/appointmentDate, so the
// previous `scheduledDate || appointmentDate || createdAt` chain always fell
// through to createdAt and put every job on the day it was sent.
function normalizeJob(raw) {
  const status    = raw.currentStatus || raw.status || '';
  const createdMs = raw.createdAt ? new Date(raw.createdAt).getTime() : NaN;
  return {
    id:          raw.serviceId || raw.id || raw.requestId || raw.serviceRequestId,
    serviceType: raw.serviceType || 'Service',
    // Display-only (custom name for "Other"); serviceType stays canonical for colors/matching.
    serviceTypeLabel: getServiceTypeLabel(raw) || 'Service',
    status,
    address:     raw.propertyAddress || raw.address || (raw.property && raw.property.address) || 'Unknown address',
    requestedDateRaw: raw.requestedDate || null,
    // Legacy SRs may still carry a due-by time; new ones are date-only.
    requestedTimeRaw: raw.requestedTime || null,
    createdMs:   Number.isFinite(createdMs) ? createdMs : 0,
    // requesterName is what the SR response actually returns; the agent*
    // fields are kept for any older payload shape. requestedBy (a raw user id)
    // is deliberately not used as a display name.
    clientName:  raw.requesterName || raw.agentName || [raw.agentFirstName, raw.agentLastName].filter(Boolean).join(' ') || 'Agent',
  };
}

// "YYYY-MM-DD" of a job's due date in the viewer's profile timezone — matches
// how the agent-side pages render the same requestedDate
// (formatInstantForViewer). A bare "YYYY-MM-DD" value is used as-is.
function jobDueDateKey(job, viewerTimezone) {
  const raw = job.requestedDateRaw;
  if (!raw) return null;
  const str = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const instant = new Date(str);
  if (Number.isNaN(instant.getTime())) return null;
  try {
    return instant.toLocaleDateString('en-CA', { timeZone: viewerTimezone });
  } catch {
    return fmtDate(instant);
  }
}

// Due dates have no time of day (the Create SR form only collects a date), so
// the card reads "Due" rather than the stored midnight ("12:00 AM PDT").
// Legacy SRs with a real due-by requestedTime still show it.
function jobTimeLabel(job, viewerTimezone) {
  if (job.requestedTimeRaw && String(job.requestedTimeRaw).indexOf('T') >= 0) {
    const t = formatInstantForViewer(job.requestedTimeRaw, viewerTimezone).timeLabel;
    if (t) return `Due by ${t}`;
  }
  return 'Due';
}

// ── SVG icons ─────────────────────────────────────────────────────────────────
const ChevLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ChevronRightIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ClockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const CalendarXIcon = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="10" y1="14" x2="14" y2="18" /><line x1="14" y1="14" x2="10" y2="18" />
  </svg>
);

// ── Main component ────────────────────────────────────────────────────────────
const CalendarPage = () => {
  const navigate = useNavigate();

  // "Now" in the viewer's own profile timezone (not the device's) — the
  // calendar's initial view, its "today" highlight, and the "Add Event"
  // form's default date/time all key off this so they agree with each
  // other and with create-service-request's identical pre-fill, even when
  // the device's local day disagrees with the viewer's zone (e.g. a device
  // set to a zone far ahead/behind the profile's zone near a day boundary —
  // the device can be on "tomorrow" while the viewer's own zone is still on
  // "today"). Computed once on mount via a lazy initializer so every
  // fresh-form open this session reuses the same "now" reading.
  const [nowInViewerZone] = useState(() => nowWallClockInViewerZone());
  const todayDate = (() => {
    const [y, m, d] = nowInViewerZone.localDate.split('-').map(Number);
    return new Date(y, m - 1, d);
  })();

  const [curYear,  setCurYear]  = useState(todayDate.getFullYear());
  const [curMonth, setCurMonth] = useState(todayDate.getMonth());
  const [selDate,  setSelDate]  = useState(todayDate);

  const [jobs,        setJobs]        = useState([]);
  const [jobsLoading,  setJobsLoading]  = useState(true);
  const [events,       setEvents]       = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(emptyForm(todayDate, nowInViewerZone.localTime));
  const [editEventId, setEditEventId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Viewer's own timezone — starts from the generic localStorage-based
  // fallback, then upgrades to this SP's own profile State (GET
  // /api/v1/users/me/sp-profile). Same pattern as AgentCalendarPage.js /
  // TransactionTimeline.js / JobsPage.js / JobDetailPage.js — this is the SP's
  // own calendar, so it's the same viewer whose Jobs pages already use this.
  const [viewerTimezone, setViewerTimezone] = useState(() => getViewerTimezone());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && typeof resp.data?.state === 'string' && resp.data.state.trim()) {
          setViewerTimezone(resolveTimezoneFromState(resp.data.state));
        }
      } catch {
        // Transient failure — keep the localStorage-based fallback.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayKey = fmtDate(todayDate);
  const selKey   = fmtDate(selDate);
  const grid     = buildGrid(curYear, curMonth);

  // Role guard
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Fetch assigned jobs once — spServices requires a status per call, so fetch
  // every calendar-relevant status in parallel (mirrors JobsPage's pattern).
  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const token = await getFirebaseToken();
      const results = await Promise.all(
        JOB_STATUSES.map(status =>
          apiClient.get(API_ENDPOINTS.spServices(status), {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then(r => extractArray(r.data))
            .catch(() => [])
        )
      );
      setJobs(results.flat().map(normalizeJob));
    } catch {
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Fetch self-created personal/blocked-time events — same generic calendar
  // events API the main app's Calendar uses, scoped to the logged-in user.
  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(`${API_BASE_URL}/api/v1/calendar/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const arr = Array.isArray(res.data?.data) ? res.data.data : [];
      // This feed also returns events the SP was only *invited* to —
      // calendar-service auto-invites every SP on a transaction to each
      // service event the requester adds to that transaction's timeline.
      // Those are the requester's events, not the SP's own: their own SRs
      // already render as job cards above, and other SPs' SRs don't belong
      // here at all. Keep only events this SP organized (created) themselves.
      // `organizer` is computed server-side against the caller's token
      // (Jackson drops the "is" from CalendarEventResponse.isOrganizer — same
      // field AgentCalendarPage.js reads); createdByUserId is the fallback.
      const myUid = auth.currentUser?.uid;
      setEvents(arr.filter(ev =>
        ev.organizer === true
        || ev.isOrganizer === true
        || (!!myUid && ev.createdByUserId === myUid)
      ));
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Calendar navigation ──────────────────────────────────────────────────────
  const prevMonth = () => {
    if (curMonth === 0) { setCurYear(y => y - 1); setCurMonth(11); }
    else setCurMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (curMonth === 11) { setCurYear(y => y + 1); setCurMonth(0); }
    else setCurMonth(m => m + 1);
  };
  const goToday = () => {
    setCurYear(todayDate.getFullYear());
    setCurMonth(todayDate.getMonth());
    setSelDate(new Date(todayDate));
  };
  const handleDayClick = (cell) => {
    if (cell.other) return;
    const d = new Date(curYear, curMonth, cell.d);
    d.setHours(0, 0, 0, 0);
    setSelDate(d);
  };

  // ── Derived: dots per day (jobs only — legend covers job categories, not
  // self-created personal events) ──────────────────────────────────────────────
  const jobDotMap = {};
  jobs.forEach(job => {
    const key = jobDueDateKey(job, viewerTimezone);
    if (!key) return;
    if (!jobDotMap[key]) jobDotMap[key] = [];
    jobDotMap[key].push(colorForJob(job));
  });

  const dayJobs = jobs
    .filter(job => jobDueDateKey(job, viewerTimezone) === selKey)
    .sort((a, b) => a.createdMs - b.createdMs);
  const dayEvents = events
    .filter(ev => eventLocalDateKey(ev, viewerTimezone) === selKey)
    .sort((a, b) => (a.scheduledTime || '').localeCompare(b.scheduledTime || ''));

  const dayItemCount = dayJobs.length + dayEvents.length;
  const isLoadingDay = jobsLoading || eventsLoading;

  // Service Requests, to resolve the correct viewer-timezone time for
  // service-type calendar events (mirrors AgentCalendarPage.js's PDT-labeled
  // display). sentServiceRequests (used on the agent's calendar) is
  // agent-scoped — "service requests the authenticated agent has sent" — and
  // is not readable by an SP account, which is why this page previously fell
  // back to the calendar event's own (possibly unconverted) scheduledTime.
  // The SP-scoped equivalent is GET /api/v1/users/me/service-requests: "my
  // inbound service requests", with an *optional* ?status= — omitting it
  // (as here) returns every status in one call, including AWAITING_ASSIGNMENT
  // which the day-jobs list above deliberately excludes. Fetched once and
  // grouped by transaction locally, rather than per-transaction per-day.
  const [allMySrs, setAllMySrs] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getFirebaseToken().catch(() => null);
      if (!token || cancelled) return;
      try {
        const res = await apiClient.get(`${API_BASE_URL}/api/v1/users/me/service-requests`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const arr = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []);
        if (!cancelled) setAllMySrs(arr);
      } catch {
        if (!cancelled) setAllMySrs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const srsByTransaction = useMemo(() => {
    const map = {};
    (allMySrs || []).forEach(sr => {
      if (!sr.transactionId) return;
      (map[sr.transactionId] = map[sr.transactionId] || []).push(sr);
    });
    return map;
  }, [allMySrs]);

  // Resolves the linked SR for a service-type event: tier-1 by
  // serviceRequestId, tier-3 by eventType match within that same
  // transaction's SR list — scoped by transactionId, so it can't cross-match
  // a different transaction's SR of the same service type.
  const resolveLinkedSr = (ev) => {
    const isServiceEvent = ev.category === 'service' || !!ev.providerType;
    if (!isServiceEvent || !ev.transactionId) return null;
    const txnSrs = srsByTransaction[ev.transactionId];
    if (!txnSrs) return null;
    if (ev.serviceRequestId) {
      const byId = txnSrs.find(sr => (sr.id || sr.requestId || sr.serviceRequestId) === ev.serviceRequestId);
      if (byId) return byId;
    }
    const evType = (ev.eventType || '').toLowerCase();
    return txnSrs.find(sr => (sr.serviceType || '').toLowerCase() === evType) || null;
  };

  // ── Job card click — routes to the Jobs page's action flow; status changes
  // happen there, not by editing the calendar entry directly. ────────────────
  const handleJobClick = (job) => {
    navigate('/jobs', { state: { tab: STATUS_TAB[job.status] || 'active' } });
  };

  // ── Add/Edit event modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditEventId(null);
    setForm(emptyForm(selDate, nowInViewerZone.localTime));
    setSaveError('');
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setSaveError(''); setEditEventId(null); };

  const openEditModal = async (ev) => {
    setEditEventId(ev.eventId);
    setForm(emptyForm(selDate));
    setSaveError('');
    setEditLoading(true);
    setShowModal(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(`${API_BASE_URL}/api/v1/calendar/events/${ev.eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data?.data || res.data || {};
      setForm({
        title:         d.title || '',
        scheduledDate: d.scheduledDate || fmtDate(selDate),
        scheduledTime: d.scheduledTime || '10:00',
        duration:      d.duration || '1 hour',
        description:   d.description || '',
        location:      d.location || '',
      });
    } catch {
      setSaveError('Failed to load event details. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      const token = await getFirebaseToken();
      // Save in UTC: convert the picker's local wall-clock values before
      // sending, and mark the event so any viewer renders it in their own
      // profile timezone.
      const { scheduledDate: utcDate, scheduledTime: utcTime } =
        toUtcWallClock(form.scheduledDate, form.scheduledTime || null);
      const payload = {
        title:         form.title,
        scheduledDate: utcDate,
        scheduledTime: utcTime,
        timeZone:      'UTC',
        duration:      form.duration,
        eventType:     'Other',
        allDay:        form.duration === 'All day',
        ...(form.description && { description: form.description }),
        ...(form.location    && { location: form.location }),
      };
      if (editEventId) {
        await apiClient.put(`${API_BASE_URL}/api/v1/calendar/events/${editEventId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await apiClient.post(`${API_BASE_URL}/api/v1/calendar/events`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      closeModal();
      fetchEvents();
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'Failed to save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(`${API_BASE_URL}/api/v1/calendar/events/${deleteConfirmId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteConfirmId(null);
      fetchEvents();
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete event. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="cp-container">
      <div className="cp-panel-wrap">

        {/* ── Left: mini calendar ── */}
        <div className="cp-left-panel">
          <div className="cp-cal-header">
            <p className="cp-cal-month">{monthLabel(curYear, curMonth)}</p>
            <div className="cp-cal-controls">
              <button type="button" className="cp-today-btn" onClick={goToday}>Today</button>
              <div className="cp-nav-btns">
                <button type="button" className="cp-nav-btn" onClick={prevMonth}><ChevLeft /></button>
                <button type="button" className="cp-nav-btn" onClick={nextMonth}><ChevRight /></button>
              </div>
            </div>
          </div>

          <div className="cp-weekdays">
            {WEEKDAYS.map((d) => <div key={d} className="cp-weekday">{d}</div>)}
          </div>

          <div className="cp-grid">
            {grid.map((cell, i) => {
              const cellKey = cell.other ? null : fmtDate(new Date(curYear, curMonth, cell.d));
              const isToday = cellKey === todayKey;
              const isSel   = cellKey === selKey;
              const dots    = (jobDotMap[cellKey] || []).slice(0, 3);
              return (
                <div
                  key={i}
                  role={cell.other ? undefined : 'button'}
                  tabIndex={cell.other ? undefined : 0}
                  onClick={() => handleDayClick(cell)}
                  onKeyDown={(e) => !cell.other && e.key === 'Enter' && handleDayClick(cell)}
                  className={[
                    'cp-grid-day',
                    cell.other ? 'cp-grid-day-other'     : 'cp-grid-day-clickable',
                    isSel      ? 'cp-grid-day-selected'  : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className={[
                    'cp-grid-num',
                    isToday          ? 'cp-grid-num-today'    : '',
                    isSel && !isToday ? 'cp-grid-num-selected' : '',
                    cell.other       ? 'cp-grid-num-other'    : '',
                  ].filter(Boolean).join(' ')}>
                    {cell.d}
                  </div>
                  {dots.length > 0 && (
                    <div className="cp-dots">
                      {dots.map((color, di) => (
                        <div key={di} className="cp-dot" style={{ background: color }} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="cp-legend">
            {LEGEND.map(({ label, color }) => (
              <div key={label} className="cp-legend-item">
                <div className="cp-dot" style={{ background: color }} />
                <span>{label}</span>
              </div>
            ))}
          </div>

          <button type="button" className="cp-add-event-btn" onClick={openAddModal}>
            <PlusIcon /> Add Event
          </button>
        </div>

        {/* ── Right: day panel ── */}
        <div className="cp-right-panel">
          <div className="cp-day-header">
            <div>
              <p className="cp-day-title">{dayLabel(selDate)}</p>
              <p className="cp-day-sub">
                {isLoadingDay ? 'Loading…' : dayItemCount === 0 ? 'Nothing scheduled' : `${dayItemCount} item${dayItemCount !== 1 ? 's' : ''} scheduled`}
              </p>
            </div>
            {!isLoadingDay && (
              <span className="cp-day-count-badge">{dayItemCount} item{dayItemCount !== 1 ? 's' : ''}</span>
            )}
          </div>

          <div className="cp-day-jobs">
            {isLoadingDay ? (
              <div className="cp-right-empty">
                <p>Loading…</p>
              </div>
            ) : dayItemCount === 0 ? (
              <div className="cp-right-empty">
                <CalendarXIcon size={36} />
                <p>Nothing on this day</p>
                <span>Click <strong>+ Add Event</strong> to schedule one.</span>
              </div>
            ) : (
              <>
                {dayJobs.map((job) => (
                  <div
                    key={job.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleJobClick(job)}
                    onKeyDown={(e) => e.key === 'Enter' && handleJobClick(job)}
                    className="cp-day-job-card"
                  >
                    <div className="cp-job-color-bar" style={{ background: colorForJob(job) }} />
                    <div className="cp-day-job-info">
                      <p className="cp-day-job-type">{job.serviceTypeLabel || job.serviceType}</p>
                      <p className="cp-day-job-addr">{job.address}</p>
                      <p className="cp-day-job-time"><ClockIcon size={12} />{jobTimeLabel(job, viewerTimezone)}</p>
                    </div>
                    <div className="cp-day-job-right">
                      <span className={`cp-pill ${(STATUS_PILL[job.status] || {}).cls || ''}`}>
                        {(STATUS_PILL[job.status] || {}).label || job.status}
                      </span>
                      <ChevronRightIcon size={14} />
                    </div>
                  </div>
                ))}

                {dayEvents.map((ev) => {
                  // Prefer the linked SR's requestedTime (always a real UTC
                  // instant) over the calendar event's own scheduledTime when
                  // resolvable — same fallback-safe pattern as
                  // AgentCalendarPage.js / TransactionTimeline.js.
                  const linkedSr = resolveLinkedSr(ev);
                  const evTimeLabel = linkedSr?.requestedTime
                    ? formatInstantForViewer(linkedSr.requestedTime, viewerTimezone).timeLabel
                    : formatEventForViewer(ev, viewerTimezone).timeLabel;
                  return (
                  <div key={ev.eventId || ev.id} className="cp-event-card">
                    <div className="cp-event-bar" />
                    <div className="cp-event-body">
                      <p className="cp-event-tag">Personal (Self-created)</p>
                      <p className="cp-event-title">{ev.title}</p>
                      {evTimeLabel && (
                        <p className="cp-day-job-time"><ClockIcon size={12} />{evTimeLabel}{ev.duration ? ` · ${ev.duration}` : ''}</p>
                      )}
                      {(ev.location || ev.description) && (
                        <div className="cp-event-meta">
                          {ev.location    && <span>📍 {ev.location}</span>}
                          {ev.description && <span>📝 {ev.description}</span>}
                        </div>
                      )}
                    </div>
                    <div className="cp-event-actions">
                      <button type="button" className="cp-icon-btn cp-icon-btn-edit" onClick={() => openEditModal(ev)} title="Edit event">
                        <EditIcon />
                      </button>
                      <button type="button" className="cp-icon-btn cp-icon-btn-delete" onClick={() => { setDeleteConfirmId(ev.eventId); setDeleteError(''); }} title="Delete event">
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Add/Edit event modal ── */}
      {showModal && (
        <div className="cp-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="cp-modal">
            <div className="cp-modal-header">
              <p className="cp-modal-title">{editEventId ? 'Edit Event' : 'Add Event'}</p>
              <button type="button" className="cp-modal-close" onClick={closeModal}><XIcon /></button>
            </div>

            <form onSubmit={handleSave} style={{ position: 'relative' }}>
              {editLoading && (
                <div className="cp-form-loading"><div className="cp-form-spinner" /></div>
              )}
              <fieldset disabled={editLoading} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
                <div className="cp-form-group">
                  <label className="cp-form-label">Title <span className="cp-req">*</span></label>
                  <input
                    className="cp-form-input"
                    type="text"
                    name="title"
                    placeholder="e.g. Blocked — Not Available"
                    value={form.title}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="cp-form-row">
                  <div className="cp-form-group">
                    <label className="cp-form-label">Date <span className="cp-req">*</span></label>
                    <input
                      className="cp-form-input"
                      type="date"
                      name="scheduledDate"
                      value={form.scheduledDate}
                      onChange={handleFormChange}
                      min={nowWallClockInViewerZone(viewerTimezone).localDate}
                      required
                    />
                  </div>
                  <div className="cp-form-group">
                    <label className="cp-form-label">Time <span className="cp-req">*</span></label>
                    <input
                      className="cp-form-input"
                      type="time"
                      name="scheduledTime"
                      value={form.scheduledTime}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="cp-form-group">
                  <label className="cp-form-label">Duration <span className="cp-req">*</span></label>
                  <select
                    className="cp-form-select"
                    name="duration"
                    value={form.duration}
                    onChange={handleFormChange}
                    required
                  >
                    {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>

                <div className="cp-form-group">
                  <label className="cp-form-label">Location <span className="cp-opt">optional</span></label>
                  <input
                    className="cp-form-input"
                    type="text"
                    name="location"
                    placeholder="e.g. 123 Main St, San Francisco"
                    value={form.location}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="cp-form-group">
                  <label className="cp-form-label">Description <span className="cp-opt">optional</span></label>
                  <textarea
                    className="cp-form-textarea"
                    name="description"
                    placeholder="Add notes…"
                    value={form.description}
                    onChange={handleFormChange}
                  />
                </div>
              </fieldset>

              {saveError && <p className="cp-save-error">{saveError}</p>}

              <div className="cp-modal-footer">
                <button type="button" className="cp-btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="cp-btn-save" disabled={saving || editLoading}>
                  {saving ? (editEventId ? 'Updating…' : 'Saving…') : (editEventId ? 'Update Event' : 'Save Event')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      {deleteConfirmId && (
        <div className="cp-modal-overlay" onClick={(e) => e.target === e.currentTarget && !deleting && setDeleteConfirmId(null)}>
          <div className="cp-modal cp-modal--confirm">
            <div className="cp-modal-header">
              <p className="cp-modal-title">Delete Event</p>
              <button type="button" className="cp-modal-close" onClick={() => !deleting && setDeleteConfirmId(null)}><XIcon /></button>
            </div>
            <div className="cp-confirm-body">
              <p>Are you sure you want to delete this event? This action cannot be undone.</p>
            </div>
            {deleteError && <p className="cp-save-error">{deleteError}</p>}
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-cancel" onClick={() => setDeleteConfirmId(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="cp-btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
